'use client'

import { useCallback, useEffect, useRef } from 'react'
import { classifyMetricError } from './metrics'
import { type ClientJourney, startClientJourney } from './metrics-browser'

type PageStatus =
  | 'LoadingFirstPage'
  | 'CanLoadMore'
  | 'LoadingMore'
  | 'Exhausted'

/** Explicit page request -> committed result, independently of first-page timing. */
export function usePaginationTelemetry(options: {
  enabled: boolean
  functionName: string
  status: PageStatus
  itemCount: number
  loadMore: (numItems: number) => void
}): (numItems: number) => void {
  const { enabled, functionName, status, itemCount, loadMore } = options
  const active = useRef<{
    journey: ClientJourney
    functionName: string
    initialCount: number
    observedLoading: boolean
  } | null>(null)

  useEffect(() => {
    const pending = active.current
    if (!pending) {
      return
    }
    if (
      !enabled ||
      pending.functionName !== functionName ||
      status === 'LoadingFirstPage'
    ) {
      pending.journey.finish('cancelled', {
        tags: { reason: 'subscription_changed' },
      })
      active.current = null
    } else if (status === 'LoadingMore') {
      pending.observedLoading = true
    } else if (
      status === 'Exhausted' ||
      itemCount !== pending.initialCount ||
      pending.observedLoading
    ) {
      // An exhausted page may contain zero rows. React may also batch away the
      // LoadingMore render for an immediately available cached page.
      pending.journey.finish('success')
      active.current = null
    }
  }, [enabled, functionName, status, itemCount])
  useEffect(
    () => () => {
      active.current?.journey.finish('cancelled', {
        tags: { reason: 'unmounted' },
      })
      active.current = null
    },
    []
  )

  return useCallback(
    (numItems: number) => {
      if (!(enabled && status === 'CanLoadMore') || active.current) {
        // The underlying hook owns no-op/coalescing semantics; instrumentation
        // must never suppress, delay, or duplicate a caller's invocation.
        loadMore(numItems)
        return
      }
      const pending = {
        functionName,
        initialCount: itemCount,
        observedLoading: false,
        journey: startClientJourney('client.query.next_page', {
          tags: {
            domain: 'data',
            surface: 'paginated_query',
            function: functionName,
            transport: 'convex_subscription',
          },
        }),
      }
      active.current = pending
      try {
        loadMore(numItems)
      } catch (error) {
        pending.journey.finish(classifyMetricError(error))
        active.current = null
        throw error
      }
    },
    [enabled, functionName, status, itemCount, loadMore]
  )
}
